import { type LearningModeTransitionState } from "@/hooks/useLearningModeTransition";
import { Check, Loader2 } from "lucide-react";
import { CollapsibleSessionBar } from "./CollapsibleSessionBar";

interface LearningModeTransitionControlProps {
  targetLanguage: string;
  state: LearningModeTransitionState;
  error?: string | null;
  onSwitch: () => void;
}

export function LearningModeTransitionControl({
  targetLanguage,
  state,
  error,
  onSwitch,
}: LearningModeTransitionControlProps) {
  const isSwitching = state === "switching";
  const isReady = state === "ready";
  const hasError = state === "error" && Boolean(error);
  const actionLabel = `Switch from translations to a ${targetLanguage} conversation`;

  const content = isSwitching ? (
    <div role="status" aria-live="polite" className="flex min-w-0 items-center gap-2.5">
      <Loader2
        size={15}
        className="shrink-0 animate-spin text-orange-200/75 motion-reduce:animate-none"
        aria-hidden="true"
      />
      <span className="min-w-0">
        <span className="block text-xs font-semibold text-white/70">
          Switching to Conversation mode...
        </span>
        <span className="block truncate text-[11px] text-white/35">
          Your tutor is keeping this session&apos;s context.
        </span>
      </span>
    </div>
  ) : isReady ? (
    <div
      role="status"
      aria-live="polite"
      className="flex min-w-0 items-center gap-2 text-xs font-semibold text-white/70"
    >
      <Check size={15} className="shrink-0 text-emerald-300/80" aria-hidden="true" />
      <span>Conversation mode ready</span>
    </div>
  ) : (
    <button
      type="button"
      data-session-bar-action={hasError ? undefined : true}
      onClick={onSwitch}
      aria-label={hasError ? error || "Try switching modes again" : actionLabel}
      className={`min-w-0 rounded-md px-2 py-1 text-left text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-300/40 ${
        hasError
          ? "text-red-200/75 hover:bg-red-300/[0.06] hover:text-red-100"
          : "text-orange-200/80 hover:bg-white/[0.055] hover:text-orange-100"
      }`}
    >
      {hasError ? error : actionLabel}
    </button>
  );

  return (
    <CollapsibleSessionBar
      label="conversation switch"
      collapsedLabel={
        isSwitching
          ? "Switching to Conversation mode..."
          : isReady
            ? "Conversation mode ready"
            : hasError
              ? "Conversation switch failed - expand to retry"
              : "Conversation option"
      }
      collapsedIcon={
        isSwitching ? (
          <Loader2
            size={13}
            className="animate-spin motion-reduce:animate-none"
            aria-hidden="true"
          />
        ) : isReady ? (
          <Check size={13} className="text-emerald-300/70" aria-hidden="true" />
        ) : null
      }
      leading={content}
      trailing={null}
      canCollapse={!isSwitching && !isReady}
    />
  );
}
